import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { ConfigProvider } from "antd";
import { XProvider } from "@ant-design/x";
import { ChatStateContext } from "./context/ChatStateContext";
import { ChatActionContext } from "./context/ChatActionContext";
import { SessionContext } from "./context/SessionContext";
import { ChatSenderStateContext } from "./context/ChatSenderStateContext";
import useADKChat from "@/hooks/useADKChat";
import { primaryBlue } from "@/styles/common";
import { cssVarStyle } from "@/styles/tokens";
import type {
  XAdkProviderProps,
  XAdkProviderHandle,
  XAdkProviderConfig,
} from "@/types/XAdkProvider";
import type { ChatHookResult } from "@/types/ChatHook";
import {
  mergeChatStrategies,
  resolvePresetUploadRequest,
} from "@/presets/runtime";

/**
 * XAdkProvider Props (扩展版)
 *
 * 支持两种使用方式:
 * 1. 标准协议: 提供 url/token/config,使用内置 useADKChat
 * 2. 自定义协议: 提供 chatData,使用自己的 Hook
 */
export interface XAdkProviderPropsExtended extends Omit<
  XAdkProviderProps,
  "url" | "token" | "config"
> {
  /**
   * 选项 A: 使用标准协议 (内置 useADKChat)
   */
  url?: string;
  token: string;
  config?: XAdkProviderConfig;

  /**
   * 选项 B: 使用自定义 Hook 数据
   * 如果提供此参数,将忽略 url/token/config
   *
   * @example
   * // GraphQL 协议
   * const chatData = useMyGraphQLChat({ endpoint: '...' });
   * <XAdkProvider chatData={chatData}>
   *   <XAdkProvider.DefaultLayout />
   * </XAdkProvider>
   *
   * @example
   * // WebSocket 协议
   * const chatData = useWebSocketChat({ url: 'ws://...' });
   * <XAdkProvider chatData={chatData}>
   *   <XAdkProvider.DefaultLayout />
   * </XAdkProvider>
   */
  chatData?: ChatHookResult;

  children: React.ReactNode;
}

/**
 * XAdkProvider - AI 聊天 SDK 核心 Provider
 *
 * 采用 Compound Components 架构:
 * - 只负责数据和状态管理
 * - 不包含 UI 布局
 * - 通过 Context 向子组件提供数据
 * - 支持完全自定义的组件组合
 * - 支持协议解耦,可使用自定义 Hook
 *
 * @example
 * // 基础用法 - 标准协议
 * <XAdkProvider url="..." token="..." config={...}>
 *   <XAdkProvider.Chatbot />
 * </XAdkProvider>
 *
 * @example
 * // 自定义协议 - GraphQL
 * function App() {
 *   const chatData = useMyGraphQLChat({ endpoint: '...' });
 *   return (
 *     <XAdkProvider chatData={chatData}>
 *       <XAdkProvider.DefaultLayout />
 *     </XAdkProvider>
 *   );
 * }
 *
 * @example
 * // 自定义布局
 * <XAdkProvider url="..." token="...">
 *   <div style={{ display: 'flex' }}>
 *     <XAdkProvider.Sidebar />
 *     <div>
 *       <XAdkProvider.Messages />
 *       <XAdkProvider.Sender />
 *     </div>
 *   </div>
 * </XAdkProvider>
 */
const XAdkProviderComponent = forwardRef<
  XAdkProviderHandle,
  XAdkProviderPropsExtended
>(
  (
    {
      url = window.location.origin,
      token,
      config,
      preset,
      strategies,
      chatData, // 自定义 Hook 数据
      providerId = "defaultId",
      onSuccess,
      onError,
      onMessage,
      onStream,
      children,
    },
    ref,
  ) => {
    const mergedStrategies = useMemo(
      () =>
        mergeChatStrategies(preset ?? config?.preset, {
          ...config?.strategies,
          ...strategies,
        }),
      [preset, strategies, config?.preset, config?.strategies],
    );

    // 内置 Hook 数据 (仅在没有提供 chatData 时调用)
    const internalData = useADKChat({
      url,
      token: token || "",
      config: config || {},
      strategies: mergedStrategies,
      onError,
      onSuccess,
      onMessage,
      onStream,
      // 仅在提供了 url 和 token 时才初始化
      enabled: !chatData && !!url && !!token,
    });

    // 使用外部数据或内部数据
    const data = chatData || internalData;

    // 解构数据
    const {
      appInfo,
      startChat,
      stopChat,
      reChat,
      clearChat,
      suggestChat,
      confirmFnCall,
      deleteSession,
      updateSession,
      currentSessionId,
      sessionPagination,
      sessionList,
      messages,
      loading,
      prologue,
      suggestions,
      initialized,
      setCurrentSession,
      setMessages,
      likeMessage = async () => false,
      setStateDelta = () => {},
    } = data;

    const showFeedback = config?.showFeedback ?? true;
    const onFileClick = config?.onFileClick;
    const allowUpload = config?.allowUpload ?? false;
    const draggable = config?.draggable ?? true;
    const presetUploadRequest = useMemo(
      () =>
        resolvePresetUploadRequest(preset ?? config?.preset, {
          url,
          token,
          appNo: config?.appNo,
        }),
      [preset, config?.preset, url, token, config?.appNo],
    );
    const uploadRequest = config?.uploadRequest ?? presetUploadRequest;

    // 暴露 API 给 ref
    useImperativeHandle(
      ref,
      () => ({
        startChat,
        stopChat,
        reChat,
        clearChat,
        suggestChat,
        deleteSession,
        updateSession,
        getAppInfo: () => appInfo,
        getMessages: () => messages,
        getSessions: () => sessionList,
        setMessages,
        setCurrentSession,
        setStateDelta,
      }),
      [
        appInfo,
        messages,
        sessionList,
        startChat,
        stopChat,
        reChat,
        clearChat,
        suggestChat,
        deleteSession,
        updateSession,
        setMessages,
        setCurrentSession,
        setStateDelta,
      ],
    );

    // 状态 Context Value - 使用 useMemo 避免不必要的 rerender
    const chatStateValue = useMemo(
      () => ({
        messages,
        loading,
        currentSessionId,
        prologue,
        suggestions,
        appInfo,
        initialized,
        url,
        token,
        showFeedback,
        onFileClick,
        uploadRequest,
        strategies: mergedStrategies,
      }),
      [
        messages,
        loading,
        currentSessionId,
        prologue,
        suggestions,
        appInfo,
        initialized,
        url,
        token,
        showFeedback,
        onFileClick,
        uploadRequest,
        mergedStrategies,
      ],
    );

    // 动作 Context Value - 函数引用稳定,不需要每次都创建新对象
    const chatActionValue = useMemo(
      () => ({
        chat: startChat,
        stopChat,
        clearChat,
        reChat,
        suggestChat,
        confirmFnCall,
        setMessages,
        likeMessage,
        setStateDelta,
      }),
      [
        startChat,
        stopChat,
        clearChat,
        reChat,
        suggestChat,
        confirmFnCall,
        setMessages,
        likeMessage,
        setStateDelta,
      ],
    );

    // 会话 Context Value
    const sessionValue = useMemo(
      () => ({
        sessionList,
        sessionPagination,
        deleteSession,
        updateSession,
        setCurrentSession,
      }),
      [
        sessionList,
        sessionPagination,
        deleteSession,
        updateSession,
        setCurrentSession,
      ],
    );

    // Sender 输入框受控状态
    const [senderValue, setSenderValue] = useState("");

    // Sender 专用状态 — 不包含 messages，避免轮询更新消息时导致 Sender 重渲染和焦点丢失
    const senderStateValue = useMemo(
      () => ({
        loading,
        uploadRequest,
        allowUpload,
        draggable,
        senderValue,
        setSenderValue,
      }),
      [loading, uploadRequest, allowUpload, draggable, senderValue],
    );

    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: appInfo?.appTheme || primaryBlue,
          },
        }}
      >
        <XProvider>
          <ChatStateContext.Provider value={chatStateValue}>
            <ChatActionContext.Provider value={chatActionValue}>
              <SessionContext.Provider value={sessionValue}>
                <ChatSenderStateContext.Provider value={senderStateValue}>
                  <div
                    style={
                      {
                        ...cssVarStyle,
                        display: "flex",
                        height: "100%",
                        width: "100%",
                        position: "relative",
                      } as React.CSSProperties
                    }
                  >
                    {children}
                  </div>
                </ChatSenderStateContext.Provider>
              </SessionContext.Provider>
            </ChatActionContext.Provider>
          </ChatStateContext.Provider>
        </XProvider>
      </ConfigProvider>
    );
  },
);

XAdkProviderComponent.displayName = "XAdkProvider";

// 导入 Compound Components
import Header from "./compound/Header";
import Sidebar from "./compound/Sidebar";
import Messages from "./compound/Messages";
import Sender from "./compound/Sender";
import Chatbot from "./compound/Chatbot";
import Welcome from "./compound/Welcome";
import DefaultLayout from "./compound/DefaultLayout";

// 导出时会挂载 Compound Components
const XAdkProvider = XAdkProviderComponent as typeof XAdkProviderComponent & {
  Header: typeof Header;
  Sidebar: typeof Sidebar;
  Messages: typeof Messages;
  Sender: typeof Sender;
  Chatbot: typeof Chatbot;
  Welcome: typeof Welcome;
  DefaultLayout: typeof DefaultLayout;
};

// 挂载 Compound Components
XAdkProvider.Header = Header;
XAdkProvider.Sidebar = Sidebar;
XAdkProvider.Messages = Messages;
XAdkProvider.Sender = Sender;
XAdkProvider.Chatbot = Chatbot;
XAdkProvider.DefaultLayout = DefaultLayout;
XAdkProvider.Welcome = Welcome;

export default XAdkProvider;
